import { coverCrop, type RGB } from './lensFilter';

/**
 * A110T — full-screen WebGL filter for « Voir le paysage ».
 * Same treatment as the maisonmata.com « Sunset Lenses » article: the camera
 * image (sRGB) multiplied by the tint layer, top to bottom (three stops,
 * linear in between, as a CSS gradient), plus a light vignette.
 * u_mix = 0 shows the camera without lens (press-and-hold comparison).
 */
const VERTEX = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

const FRAGMENT = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_tex;
uniform vec2 u_scale;
uniform vec2 u_offset;
uniform float u_mirror;
uniform vec3 u_f0;
uniform vec3 u_f1;
uniform vec3 u_f2;
uniform float u_mix;
uniform float u_vignette;
uniform float u_aspect;

void main() {
  float x = u_mirror > 0.5 ? 1.0 - v_uv.x : v_uv.x;
  vec2 tuv = vec2(x, v_uv.y) * u_scale + u_offset;
  vec3 camera = texture2D(u_tex, tuv).rgb;
  float y = 1.0 - v_uv.y;
  vec3 factor = y < 0.5 ? mix(u_f0, u_f1, y * 2.0) : mix(u_f1, u_f2, (y - 0.5) * 2.0);
  vec2 d = (v_uv - 0.5) * vec2(u_aspect, 1.0);
  float r = length(d) / length(vec2(u_aspect, 1.0) * 0.5);
  float vignette = 1.0 - u_vignette * smoothstep(0.5, 1.05, r);
  gl_FragColor = vec4(mix(camera, camera * factor * vignette, u_mix), 1.0);
}`;

export interface LensUniforms {
  /** Multiply factors at the top, middle and bottom of the view. */
  stops: readonly [RGB, RGB, RGB];
  /** 1 = through the lens, 0 = bare camera. */
  mix: number;
  mirror: boolean;
  vignette: number;
}

export class LensRenderer {
  private readonly gl: WebGLRenderingContext;
  private readonly program: WebGLProgram;
  private readonly texture: WebGLTexture;
  private readonly loc: Record<string, WebGLUniformLocation | null> = {};
  private hasFrame = false;

  /** Returns null when WebGL is unavailable (the view then uses the CSS fallback). */
  static create(canvas: HTMLCanvasElement): LensRenderer | null {
    const gl = canvas.getContext('webgl', { alpha: false, antialias: false, depth: false, stencil: false, preserveDrawingBuffer: false, powerPreference: 'low-power' }) as WebGLRenderingContext | null;
    if (!gl) return null;
    try {
      return new LensRenderer(gl);
    } catch {
      return null;
    }
  }

  private constructor(gl: WebGLRenderingContext) {
    this.gl = gl;
    const compile = (type: number, source: string) => {
      const shader = gl.createShader(type);
      if (!shader) throw new Error('shader');
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) ?? 'shader');
      return shader;
    };
    const program = gl.createProgram();
    if (!program) throw new Error('program');
    gl.attachShader(program, compile(gl.VERTEX_SHADER, VERTEX));
    gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAGMENT));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) ?? 'link');
    this.program = program;
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const pos = gl.getAttribLocation(program, 'a_pos');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

    const texture = gl.createTexture();
    if (!texture) throw new Error('texture');
    this.texture = texture;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    for (const name of ['u_tex', 'u_scale', 'u_offset', 'u_mirror', 'u_f0', 'u_f1', 'u_f2', 'u_mix', 'u_vignette', 'u_aspect']) {
      this.loc[name] = gl.getUniformLocation(program, name);
    }
    gl.uniform1i(this.loc.u_tex!, 0);
  }

  get lost(): boolean {
    return this.gl.isContextLost();
  }

  /** Matches the drawing buffer to the canvas size (device pixels, capped). */
  resize(cssWidth: number, cssHeight: number): void {
    const canvas = this.gl.canvas as HTMLCanvasElement;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = Math.max(1, Math.round(cssWidth * dpr));
    let h = Math.max(1, Math.round(cssHeight * dpr));
    // About 2.4 megapixels at most: plenty for a camera image, light on the GPU.
    const cap = Math.sqrt(2_400_000 / (w * h));
    if (cap < 1) { w = Math.round(w * cap); h = Math.round(h * cap); }
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
  }

  /** Uploads the current video frame (when it has one) and draws the filtered view. */
  draw(video: HTMLVideoElement, u: LensUniforms, uploadFrame: boolean): boolean {
    const gl = this.gl;
    if (gl.isContextLost()) return false;
    const vw = video.videoWidth, vh = video.videoHeight;
    if (uploadFrame && vw > 0 && video.readyState >= 2) {
      gl.bindTexture(gl.TEXTURE_2D, this.texture);
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
        this.hasFrame = true;
      } catch {
        return false;
      }
    }
    if (!this.hasFrame) return false;
    const canvas = gl.canvas as HTMLCanvasElement;
    gl.viewport(0, 0, canvas.width, canvas.height);
    const crop = coverCrop(vw, vh, canvas.width, canvas.height);
    gl.uniform2f(this.loc.u_scale!, crop.scale[0], crop.scale[1]);
    gl.uniform2f(this.loc.u_offset!, crop.offset[0], crop.offset[1]);
    gl.uniform1f(this.loc.u_mirror!, u.mirror ? 1 : 0);
    const [f0, f1, f2] = u.stops;
    gl.uniform3f(this.loc.u_f0!, f0[0], f0[1], f0[2]);
    gl.uniform3f(this.loc.u_f1!, f1[0], f1[1], f1[2]);
    gl.uniform3f(this.loc.u_f2!, f2[0], f2[1], f2[2]);
    gl.uniform1f(this.loc.u_mix!, u.mix);
    gl.uniform1f(this.loc.u_vignette!, u.vignette);
    gl.uniform1f(this.loc.u_aspect!, canvas.width / Math.max(1, canvas.height));
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    return true;
  }

  dispose(): void {
    const gl = this.gl;
    try {
      gl.deleteTexture(this.texture);
      gl.deleteProgram(this.program);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    } catch {
      /* already gone */
    }
  }
}
